import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Drawer,
  Divider,
  Chip,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import CategorySelect from './CategorySelect';
import ContainerCategorySelect from './ContainerCategorySelect';
import LocationSelect from './LocationSelect';
import { ContainerCategory, ProductCategory } from '../../../core/interfaces/category.interface';

export interface FilterField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'category' | 'location' | 'containerCategory' | 'autocomplete';
  options?: Array<{ id: number | string; name: string; description?: string }>;
  placeholder?: string;
  categories?: ProductCategory[]; // Para tipo 'category'
  containerCategories?: ContainerCategory[]; // Para tipo 'containerCategory'
  locations?: Array<{ id: number | string; code?: string; name?: string; description?: string; warehouse_code?: string }>; // Para tipo 'location'
  specialOption?: { id: string; name: string; description: string }; // Para tipo 'location' - opción especial como "Sin Ubicar"
  autocompleteOptions?: Array<{ id: number | string; name: string; description?: string }>; // Para tipo 'autocomplete'
}

interface FilterDrawerProps<T extends Record<string, any>> {
  filters: T;
  onFiltersChange: (filters: T) => void;
  onApplyFilters: (filters?: T) => void;
  onClearFilters: () => void;
  fields: FilterField[];
  loading?: boolean;
  disabled?: boolean;
}

export function FilterDrawer<T extends Record<string, any>>({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  fields,
  loading = false,
  disabled = false,
}: FilterDrawerProps<T>) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<T>(filters);

  // Sincronizar el estado local con los filtros externos
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (field: string, value: any) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleApplyFilters = () => {
    // Pasar los filtros locales al callback para asegurar que se usen los más recientes
    onApplyFilters(localFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {} as T;
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.keys(filters).some(
    (key) => filters[key] !== undefined && filters[key] !== '' && filters[key] !== null
  );

  const activeFiltersCount = Object.keys(filters).filter(
    (key) => filters[key] !== undefined && filters[key] !== '' && filters[key] !== null
  ).length;

  return (
    <Box>
      {/* Botón de filtros */}
      <Button
        variant="outlined"
        startIcon={<FilterIcon />}
        onClick={() => setFiltersOpen(true)}
        disabled={disabled}
        sx={{
          minWidth: 120,
          height: 48,
          borderRadius: 2,
          borderColor: hasActiveFilters ? 'primary.main' : 'grey.300',
          color: hasActiveFilters ? 'primary.main' : 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          },
        }}
      >
        Filtros
        {hasActiveFilters && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              ml: 1,
            }}
          />
        )}
      </Button>

      {/* Drawer de filtros */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${activeFiltersCount} activo${activeFiltersCount !== 1 ? 's' : ''}`}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <IconButton onClick={() => setFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {fields.map((field) => (
            <Grid item xs={12} key={field.key}>
              {field.type === 'text' ? (
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  value={localFilters[field.key] || ''}
                  onChange={(e) => handleFilterChange(field.key, e.target.value || undefined)}
                  placeholder={field.placeholder || `Buscar por ${field.label.toLowerCase()}`}
                />
              ) : field.type === 'date' ? (
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  type="date"
                  value={localFilters[field.key] || ''}
                  onChange={(e) => handleFilterChange(field.key, e.target.value || undefined)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              ) : field.type === 'category' ? (
                <CategorySelect
                  value={localFilters[field.key] || undefined}
                  onChange={(newValue) => {
                    // Si es 'all', convertir a undefined para que no filtre
                    const filterValue = newValue === 'all' ? undefined : newValue;
                    handleFilterChange(field.key, filterValue);
                  }}
                  categories={field.categories || []}
                  label={field.label}
                  showAllOption={true}
                  allOptionLabel="Todos"
                  size="small"
                  disabled={disabled}
                />
              ) : field.type === 'containerCategory' ? (
                <ContainerCategorySelect
                  value={localFilters[field.key] || undefined}
                  onChange={(newValue) => {
                    // Si es 'all', convertir a undefined para que no filtre
                    const filterValue = newValue === 'all' ? undefined : newValue;
                    handleFilterChange(field.key, filterValue);
                  }}
                  categories={field.containerCategories || []}
                  label={field.label}
                  showAllOption={true}
                  allOptionLabel="Todos"
                  size="small"
                  disabled={disabled}
                />
              ) : field.type === 'location' ? (
                <LocationSelect
                  value={localFilters[field.key] || undefined}
                  onChange={(newValue) => {
                    // Si es 'all', convertir a undefined para que no filtre
                    const filterValue = newValue === 'all' ? undefined : newValue;
                    handleFilterChange(field.key, filterValue);
                  }}
                  locations={field.locations || []}
                  label={field.label}
                  showAllOption={true}
                  allOptionLabel="Todos"
                  size="small"
                  disabled={disabled}
                  specialOption={field.specialOption}
                />
              ) : field.type === 'autocomplete' ? (
                <Autocomplete
                  freeSolo
                  options={field.autocompleteOptions || []}
                  getOptionLabel={(option: any) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.description || String(option.id);
                  }}
                  value={localFilters[field.key] || null}
                  onChange={(_, value: any) => {
                    handleFilterChange(field.key, value ? (typeof value === 'string' ? value : value.name || value.description || value.id) : undefined);
                  }}
                  onInputChange={(_, value) => {
                    if (value) {
                      handleFilterChange(field.key, value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      placeholder={field.placeholder || `Buscar por ${field.label.toLowerCase()}`}
                      size="small"
                    />
                  )}
                  disabled={disabled}
                  clearOnEscape
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>{field.label}</InputLabel>
                  <Select
                    value={localFilters[field.key] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Manejar el caso especial de 'sin-ubicar' para contenedores
                      if (value === 'sin-ubicar') {
                        handleFilterChange(field.key, 'sin-ubicar');
                      } else {
                        handleFilterChange(field.key, value || undefined);
                      }
                    }}
                    label={field.label}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {field.options?.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description || option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          ))}
        </Grid>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            startIcon={<ClearIcon />}
            disabled={!hasActiveFilters}
            sx={{ borderRadius: 2 }}
          >
            Limpiar Filtros
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleApplyFilters();
              setFiltersOpen(false);
            }}
            fullWidth
            disabled={loading || disabled}
            sx={{ borderRadius: 2 }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}

